import { supabase } from "@/shared/db/supabase-client";

/**
 * Interface for document_requirements table row
 */
export interface DocumentRequirementRow {
  id: string;
  jurisdiction_code: string;
  document_type: string;
  required_clauses: {
    es: string[];
    en: string[];
  };
  legal_framework_codes: string[];
  format_description_es: string;
  format_description_en: string;
  notes_es: string | null;
  notes_en: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Parsed document requirements with proper types
 */
export interface DocumentRequirements {
  id: string;
  jurisdictionCode: string;
  documentType: string;
  requiredClauses: {
    es: string[];
    en: string[];
  };
  legalFrameworkCodes: string[];
  formatDescriptionEs: string;
  formatDescriptionEn: string;
  notesEs: string | null;
  notesEn: string | null;
  isActive: boolean;
}

/**
 * Maps database row to DocumentRequirements interface
 */
function mapRowToDocumentRequirements(
  row: DocumentRequirementRow
): DocumentRequirements {
  return {
    id: row.id,
    jurisdictionCode: row.jurisdiction_code,
    documentType: row.document_type,
    requiredClauses:
      typeof row.required_clauses === "string"
        ? JSON.parse(row.required_clauses)
        : row.required_clauses,
    legalFrameworkCodes: row.legal_framework_codes,
    formatDescriptionEs: row.format_description_es,
    formatDescriptionEn: row.format_description_en,
    notesEs: row.notes_es,
    notesEn: row.notes_en,
    isActive: row.is_active,
  };
}

/**
 * Get document requirements for a specific country and document type
 * @param countryCode - Country code (e.g., "RD")
 * @param documentType - Document type (e.g., "contract-work")
 * @returns Document requirements or null if not found
 */
export async function getDocumentRequirements(
  countryCode: string,
  documentType: string
): Promise<DocumentRequirements | null> {
  const { data, error } = await (supabase as any)
    .from("document_requirements")
    .select("*")
    .eq("jurisdiction_code", countryCode.toUpperCase())
    .eq("document_type", documentType)
    .eq("is_active", true)
    .single();

  if (error) {
    console.error("Error fetching document requirements:", error);
    return null;
  }

  if (!data) return null;

  return mapRowToDocumentRequirements(data as DocumentRequirementRow);
}

/**
 * Get all document requirements for a country
 * @param countryCode - Country code (e.g., "RD")
 * @returns Array of document requirements
 */
export async function getCountryDocumentRequirements(
  countryCode: string
): Promise<DocumentRequirements[]> {
  const { data, error } = await (supabase as any)
    .from("document_requirements")
    .select("*")
    .eq("jurisdiction_code", countryCode.toUpperCase())
    .eq("is_active", true)
    .order("document_type");

  if (error) {
    console.error("Error fetching country document requirements:", error);
    return [];
  }

  return (data as DocumentRequirementRow[]).map(mapRowToDocumentRequirements);
}

/**
 * Get all active document types available for a country
 * @param countryCode - Country code
 * @returns Array of document type IDs available for this country
 */
export async function getAvailableDocumentTypesForCountry(
  countryCode: string
): Promise<string[]> {
  const requirements = await getCountryDocumentRequirements(countryCode);
  return requirements.map((r) => r.documentType);
}

/**
 * Check if a document type is available for a country
 * @param countryCode - Country code
 * @param documentType - Document type
 * @returns Boolean indicating if the document type is available
 */
export async function isDocumentTypeAvailableForCountry(
  countryCode: string,
  documentType: string
): Promise<boolean> {
  const requirements = await getDocumentRequirements(countryCode, documentType);
  return requirements !== null;
}
